import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import { Plus, Pencil, Trash2, Loader2, Car, Truck, ArrowLeft, MapPin, Shield, DollarSign, Calendar, User, Building2, Hash, Upload, Image, FileText, Download, X, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);

const VEHICLE_TYPES = ['Car', 'Truck', 'SUV', 'Motorcycle', 'Boat', 'Other'];
const CATEGORIES = ['Business', 'Personal', 'Investment'];

function VehicleDialog({ open, onOpenChange, vehicle, companyId, onSave }) {
  const [form, setForm] = useState({
    vehicle_type: 'Car', year: '', make: '', model: '', color: '', vin: '',
    category: 'Business', owner: '', location: '', associated_business: '',
    insurance_company: '', policy_number: '',
    purchase_price: '', value: '', purchase_date: '',
    registration_link: '', title_link: '', has_title: false,
    notes: '', include_financial: true
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (vehicle) {
        setForm({
          vehicle_type: vehicle.vehicle_type || 'Car', year: vehicle.year || '', make: vehicle.make || '',
          model: vehicle.model || '', color: vehicle.color || '', vin: vehicle.vin || '',
          category: vehicle.category || 'Business', owner: vehicle.owner || '',
          location: vehicle.location || '', associated_business: vehicle.associated_business || '',
          insurance_company: vehicle.insurance_company || '', policy_number: vehicle.policy_number || '',
          purchase_price: vehicle.purchase_price != null ? String(vehicle.purchase_price) : '',
          value: vehicle.value != null ? String(vehicle.value) : '',
          purchase_date: vehicle.purchase_date || '',
          registration_link: vehicle.registration_link || '', title_link: vehicle.title_link || '',
          has_title: vehicle.has_title === true,
          notes: vehicle.notes_text || '', include_financial: vehicle.include_financial !== false
        });
      } else {
        setForm({ vehicle_type: 'Car', year: '', make: '', model: '', color: '', vin: '', category: 'Business', owner: '', location: '', associated_business: '', insurance_company: '', policy_number: '', purchase_price: '', value: '', purchase_date: '', registration_link: '', title_link: '', has_title: false, notes: '', include_financial: true });
      }
    }
  }, [vehicle, open]);

  const handleSubmit = async () => {
    if (!form.make.trim()) { toast.error('Make is required'); return; }
    setSubmitting(true);
    try {
      const name = `${form.year} ${form.make}`.trim();
      const tags = [form.category.toLowerCase()];
      if (form.associated_business) tags.push(form.associated_business);
      const payload = {
        company_id: companyId, name, asset_type: 'vehicle',
        value: parseFloat(form.value) || 0,
        institution: form.location || null,
        account_number: form.vin || null,
        notes: JSON.stringify({
          make: form.make, model: form.model, year: form.year, vehicle_type: form.vehicle_type,
          color: form.color, vin: form.vin, category: form.category,
          owner: form.owner, location: form.location, associated_business: form.associated_business,
          insurance_company: form.insurance_company, policy_number: form.policy_number,
          purchase_price: parseFloat(form.purchase_price) || 0, purchase_date: form.purchase_date,
          registration_link: form.registration_link, title_link: form.title_link,
          has_title: !!form.has_title,
          notes: form.notes, include_financial: form.include_financial
        }),
        tags
      };
      if (vehicle) {
        await axios.put(`${API}/api/assets/${vehicle.id}`, payload, { withCredentials: true });
        toast.success('Vehicle updated');
      } else {
        await axios.post(`${API}/api/assets`, payload, { withCredentials: true });
        toast.success('Vehicle added');
      }
      onSave(); onOpenChange(false);
    } catch { toast.error('Failed to save'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{vehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle></DialogHeader>
        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Basic Information</h3>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Vehicle Type *</Label>
                <Select value={form.vehicle_type} onValueChange={v => setForm(p => ({...p, vehicle_type: v}))}>
                  <SelectTrigger data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{VEHICLE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Year *</Label><Input value={form.year} onChange={e => setForm(p => ({...p, year: e.target.value}))} placeholder="1969" data-testid="vehicle-year" /></div>
              <div><Label>Make *</Label><Input value={form.make} onChange={e => setForm(p => ({...p, make: e.target.value}))} placeholder="Oldsmobile" data-testid="vehicle-make" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div><Label>Model *</Label><Input value={form.model} onChange={e => setForm(p => ({...p, model: e.target.value}))} placeholder="Cutlass Convertible" data-testid="vehicle-model" /></div>
              <div><Label>Color</Label><Input value={form.color} onChange={e => setForm(p => ({...p, color: e.target.value}))} placeholder="Silver" data-testid="vehicle-color" /></div>
              <div><Label>VIN</Label><Input value={form.vin} onChange={e => setForm(p => ({...p, vin: e.target.value}))} placeholder="1HGBH41JXMN109186" data-testid="vehicle-vin" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div><Label>Category *</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({...p, category: v}))}>
                  <SelectTrigger data-testid="vehicle-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Ownership & Location */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Ownership & Location</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Owner Name</Label><Input value={form.owner} onChange={e => setForm(p => ({...p, owner: e.target.value}))} placeholder="Dillon Keane LLC" data-testid="vehicle-owner" /></div>
              <div><Label>Location</Label><Input value={form.location} onChange={e => setForm(p => ({...p, location: e.target.value}))} placeholder="Daniels Shop" data-testid="vehicle-location" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><Label>Associated Business</Label><Input value={form.associated_business} onChange={e => setForm(p => ({...p, associated_business: e.target.value}))} placeholder="Dillon Keane LLC" data-testid="vehicle-business" /></div>
            </div>
          </div>

          {/* Insurance */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Insurance</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Insurance Company</Label><Input value={form.insurance_company} onChange={e => setForm(p => ({...p, insurance_company: e.target.value}))} placeholder="State Farm" data-testid="vehicle-insurance" /></div>
              <div><Label>Policy Number</Label><Input value={form.policy_number} onChange={e => setForm(p => ({...p, policy_number: e.target.value}))} placeholder="Policy #" data-testid="vehicle-policy" /></div>
            </div>
          </div>

          {/* Value */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Value</h3>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Purchase Price</Label><Input type="number" value={form.purchase_price} onChange={e => setForm(p => ({...p, purchase_price: e.target.value}))} placeholder="0" data-testid="vehicle-purchase-price" /></div>
              <div><Label>Current Value *</Label><Input type="number" value={form.value} onChange={e => setForm(p => ({...p, value: e.target.value}))} placeholder="60000" data-testid="vehicle-value" /></div>
              <div><Label>Purchase Date</Label><Input type="date" value={form.purchase_date} onChange={e => setForm(p => ({...p, purchase_date: e.target.value}))} data-testid="vehicle-purchase-date" /></div>
            </div>
          </div>

          {/* Documents */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Documents</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Registration Link</Label><Input value={form.registration_link} onChange={e => setForm(p => ({...p, registration_link: e.target.value}))} placeholder="Paste link to registration doc" data-testid="vehicle-registration-link" /></div>
              <div><Label>Title Link</Label><Input value={form.title_link} onChange={e => setForm(p => ({...p, title_link: e.target.value}))} placeholder="Paste link to title doc" data-testid="vehicle-title-link" /></div>
            </div>
            <label className="mt-3 flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!form.has_title}
                onChange={e => setForm(p => ({...p, has_title: e.target.checked}))}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                data-testid="vehicle-has-title-checkbox"
              />
              <span className="text-sm text-gray-700">Title in hand</span>
              <span className="text-xs text-gray-400">(check if you physically possess the title)</span>
            </label>
          </div>

          {/* Notes */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Notes</h3>
            <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Additional notes..."
              className="w-full min-h-[80px] rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y" data-testid="vehicle-notes" />
          </div>

          {/* Include in financial statement */}
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.include_financial} onChange={e => setForm(p => ({...p, include_financial: e.target.checked}))} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-testid="vehicle-include-financial" />
            <label className="text-sm text-gray-700">Include in financial statement</label>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="save-vehicle-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : vehicle ? 'Update Vehicle' : 'Add Vehicle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function parseVehicleNotes(notesStr) {
  try { return JSON.parse(notesStr); } catch { return {}; }
}

export default function VehiclesPage() {
  const { currentCompany } = useCompany();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [vehicleFiles, setVehicleFiles] = useState({ photos: [] });
  const [uploading, setUploading] = useState(null);
  const photoInputRef = useRef(null);

  const fetchVehicles = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/assets`, { params: { company_id: currentCompany.id, asset_type: 'vehicle' }, withCredentials: true });
      setVehicles(res.data.map(v => {
        const meta = parseVehicleNotes(v.notes);
        return {
          ...v,
          make: meta.make || v.name?.split(' ').slice(1).join(' ') || '',
          model: meta.model || '',
          year: meta.year || v.name?.split(' ')[0] || '',
          vehicle_type: meta.vehicle_type || 'Car',
          color: meta.color || '',
          owner: meta.owner || '',
          location: meta.location || v.institution || '',
          insurance_company: meta.insurance_company || meta.insurance || '',
          policy_number: meta.policy_number || '',
          vin: meta.vin || v.account_number || '',
          category: meta.category || 'Business',
          associated_business: meta.associated_business || '',
          purchase_price: meta.purchase_price || 0,
          purchase_date: meta.purchase_date || '',
          registration_link: meta.registration_link || '',
          title_link: meta.title_link || '',
          has_title: meta.has_title === true,
          notes_text: meta.notes || '',
          include_financial: meta.include_financial !== false
        };
      }));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [currentCompany]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  // Fetch files for selected vehicle
  const fetchVehicleFiles = useCallback(async (vehicleId) => {
    if (!currentCompany || !vehicleId) return;
    try {
      const res = await axios.get(`${API}/api/files`, { params: { company_id: currentCompany.id, tag: `vehicle-${vehicleId}` }, withCredentials: true });
      const files = res.data;
      setVehicleFiles({
        photos: files.filter(f => f.tag === `vehicle-${vehicleId}-photo`),
      });
    } catch { setVehicleFiles({ photos: [] }); }
  }, [currentCompany]);

  useEffect(() => { if (selectedVehicle) fetchVehicleFiles(selectedVehicle.id); }, [selectedVehicle, fetchVehicleFiles]);

  const handleVehicleFileUpload = async (e, vehicleId, fileType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (10MB max)'); return; }
    setUploading(fileType);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const tag = `vehicle-${vehicleId}-${fileType}`;
      await axios.post(`${API}/api/files/upload?company_id=${currentCompany.id}&tag=${tag}`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${fileType === 'photo' ? 'Photo' : fileType === 'registration' ? 'Registration' : 'Title'} uploaded`);
      fetchVehicleFiles(vehicleId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); e.target.value = ''; }
  };

  const handleDeleteVehicleFile = async (fileId) => {
    try {
      await axios.delete(`${API}/api/files/${fileId}`, { withCredentials: true });
      toast.success('File deleted');
      if (selectedVehicle) fetchVehicleFiles(selectedVehicle.id);
    } catch { toast.error('Failed to delete'); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/assets/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Vehicle deleted'); setDeleteTarget(null); fetchVehicles();
    } catch { toast.error('Failed to delete'); }
  };

  const totalValue = vehicles.reduce((s, v) => s + (v.value || 0), 0);

  const filteredVehicles = vehicles.filter(v => {
    if (typeFilter !== 'all' && v.vehicle_type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (v.year || '').toLowerCase().includes(q) ||
        (v.make || '').toLowerCase().includes(q) ||
        (v.model || '').toLowerCase().includes(q) ||
        (v.color || '').toLowerCase().includes(q) ||
        (v.owner || '').toLowerCase().includes(q) ||
        (v.location || '').toLowerCase().includes(q);
    }
    return true;
  });
  const uniqueTypes = [...new Set(vehicles.map(v => v.vehicle_type))];

  if (!currentCompany) return null;
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>;

  // Detail View
  if (selectedVehicle) {
    const v = selectedVehicle;
    const Icon = v.vehicle_type === 'Truck' ? Truck : Car;
    return (
      <div className="space-y-6" data-testid="vehicle-detail">
        <button onClick={() => setSelectedVehicle(null)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-testid="back-to-vehicles">
          <ArrowLeft className="w-4 h-4" /><span className="text-sm">Back to Vehicles</span>
        </button>

        {/* Hero */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-8 relative overflow-hidden">
          <div className="relative z-10 flex items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{v.year} {v.make} {v.model}</h1>
              <div className="flex items-center gap-3 mt-2">
                {v.tags?.map((tag, i) => <span key={i} className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">{tag}</span>)}
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">{v.vehicle_type}</span>
                {v.color && <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">{v.color}</span>}
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    v.has_title ? 'bg-emerald-400/30 text-white' : 'bg-amber-400/30 text-white'
                  }`}
                >
                  {v.has_title ? 'Title' : 'No Title'}
                </span>
              </div>
            </div>
          </div>
          <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-10">
            <Icon className="w-32 h-32 text-white" />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setEditVehicle(v); }} data-testid="detail-edit-btn">
            <Pencil className="w-4 h-4 mr-2" />Edit
          </Button>
          <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDeleteTarget(v)} data-testid="detail-delete-btn">
            <Trash2 className="w-4 h-4 mr-2" />Delete
          </Button>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Value Section */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">Value</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><DollarSign className="w-4 h-4 text-blue-600" /></div>
                  <span className="text-sm text-gray-600">Current Value</span>
                </div>
                <span className="text-xl font-bold text-gray-900">{fmtCurrency(v.value)}</span>
              </div>
              {v.purchase_price > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center"><DollarSign className="w-4 h-4 text-gray-500" /></div>
                    <span className="text-sm text-gray-600">Purchase Price</span>
                  </div>
                  <span className="text-lg font-semibold text-gray-700">{fmtCurrency(v.purchase_price)}</span>
                </div>
              )}
              {v.purchase_date && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center"><Calendar className="w-4 h-4 text-gray-500" /></div>
                    <span className="text-sm text-gray-600">Purchase Date</span>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{v.purchase_date}</span>
                </div>
              )}
            </div>
          </div>

          {/* Ownership Section */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">Ownership & Location</h3>
            <div className="space-y-4">
              {v.owner && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center"><User className="w-4 h-4 text-indigo-600" /></div>
                  <div><p className="text-xs text-gray-400">Owner</p><p className="text-sm font-medium text-gray-900">{v.owner}</p></div>
                </div>
              )}
              {v.location && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center"><MapPin className="w-4 h-4 text-indigo-600" /></div>
                  <div><p className="text-xs text-gray-400">Location</p><p className="text-sm font-medium text-gray-900">{v.location}</p></div>
                </div>
              )}
              {v.associated_business && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center"><Building2 className="w-4 h-4 text-indigo-600" /></div>
                  <div><p className="text-xs text-gray-400">Associated Business</p><p className="text-sm font-medium text-gray-900">{v.associated_business}</p></div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center"><span className="text-xs font-bold text-gray-500">{v.category?.charAt(0)}</span></div>
                <div><p className="text-xs text-gray-400">Category</p><p className="text-sm font-medium text-gray-900">{v.category}</p></div>
              </div>
            </div>
          </div>

          {/* Insurance Section */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">Insurance</h3>
            {v.insurance_company ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Shield className="w-4 h-4 text-blue-600" /></div>
                  <div><p className="text-xs text-gray-400">Insurance Company</p><p className="text-sm font-medium text-gray-900">{v.insurance_company}</p></div>
                </div>
                {v.policy_number && (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center"><Hash className="w-4 h-4 text-gray-500" /></div>
                    <div><p className="text-xs text-gray-400">Policy Number</p><p className="text-sm font-medium text-gray-900">{v.policy_number}</p></div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No insurance information</p>
            )}
          </div>

          {/* Vehicle Details Section */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">Vehicle Details</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-400">Year</p><p className="text-sm font-medium text-gray-900">{v.year || '-'}</p></div>
                <div><p className="text-xs text-gray-400">Make</p><p className="text-sm font-medium text-gray-900">{v.make || '-'}</p></div>
                <div><p className="text-xs text-gray-400">Model</p><p className="text-sm font-medium text-gray-900">{v.model || '-'}</p></div>
                <div><p className="text-xs text-gray-400">Color</p><p className="text-sm font-medium text-gray-900">{v.color || '-'}</p></div>
                <div><p className="text-xs text-gray-400">Type</p><p className="text-sm font-medium text-gray-900">{v.vehicle_type}</p></div>
                {v.vin && <div><p className="text-xs text-gray-400">VIN</p><p className="text-sm font-medium text-gray-900 font-mono">{v.vin}</p></div>}
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        {v.notes_text && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Notes</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{v.notes_text}</p>
          </div>
        )}

        {/* Documents - Registration & Title as links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Registration */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-2 mb-4"><FileText className="w-4 h-4" />Registration</h3>
            {v.registration_link ? (
              <a href={v.registration_link} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors group" data-testid="registration-link">
                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-900">Registration Document</p>
                  <p className="text-xs text-blue-600 truncate">{v.registration_link}</p>
                </div>
                <Download className="w-4 h-4 text-blue-400 group-hover:text-blue-600 flex-shrink-0" />
              </a>
            ) : (
              <div className="text-center py-6 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                <FileText className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">No registration link</p>
                <p className="text-xs mt-1">Edit vehicle to add a link</p>
              </div>
            )}
          </div>

          {/* Title */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-2 mb-4"><FileText className="w-4 h-4" />Title Document</h3>
            {v.title_link ? (
              <a href={v.title_link} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors group" data-testid="title-link">
                <FileText className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-indigo-900">Title Document</p>
                  <p className="text-xs text-indigo-600 truncate">{v.title_link}</p>
                </div>
                <Download className="w-4 h-4 text-indigo-400 group-hover:text-indigo-600 flex-shrink-0" />
              </a>
            ) : (
              <div className="text-center py-6 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                <FileText className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">No title link</p>
                <p className="text-xs mt-1">Edit vehicle to add a link</p>
              </div>
            )}
          </div>
        </div>

        {/* Photos */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-2"><Image className="w-4 h-4" />Photos ({vehicleFiles.photos.length})</h3>
            <div>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleVehicleFileUpload(e, v.id, 'photo')} data-testid="vehicle-photo-input" />
              <Button variant="outline" size="sm" onClick={() => photoInputRef.current?.click()} disabled={uploading === 'photo'} data-testid="upload-photo-btn">
                {uploading === 'photo' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                Upload Photo
              </Button>
            </div>
          </div>
          {vehicleFiles.photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {vehicleFiles.photos.map(f => (
                <div key={f.id} className="relative group rounded-lg overflow-hidden border border-gray-200" data-testid={`photo-${f.id}`}>
                  <img src={`${API}/api/files/${f.id}/download`} alt={f.filename} className="w-full h-32 object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <a href={`${API}/api/files/${f.id}/download`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg bg-white/20 hover:bg-white/40 text-white"><Download className="w-4 h-4" /></a>
                    <button onClick={() => handleDeleteVehicleFile(f.id)} className="p-1.5 rounded-lg bg-white/20 hover:bg-red-500/80 text-white"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <p className="text-xs text-gray-500 p-1.5 truncate">{f.filename}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
              <Image className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">No photos yet</p>
              <p className="text-xs mt-1">Upload photos of your vehicle</p>
            </div>
          )}
        </div>

        {/* Dialogs still need to be here for edit/delete from detail view */}
        <VehicleDialog open={!!editVehicle} onOpenChange={v => { if (!v) setEditVehicle(null); }}
          vehicle={editVehicle} companyId={currentCompany?.id} onSave={() => { fetchVehicles(); setSelectedVehicle(null); }} />
        <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Delete Vehicle</AlertDialogTitle>
              <AlertDialogDescription>Delete "{deleteTarget?.year} {deleteTarget?.make} {deleteTarget?.model}"?</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => { handleDelete(); setSelectedVehicle(null); }} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Vehicles</h1>
          <p className="text-gray-500 text-sm mt-0.5">Track your vehicle inventory and details</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-vehicle-btn">
          <Plus className="w-4 h-4 mr-2" />Add Vehicle
        </Button>
      </div>

      {/* Total Value Banner */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-6 relative overflow-hidden">
        <div className="relative z-10">
          <p className="text-blue-100 text-sm">Total Vehicle Value</p>
          <p className="text-4xl font-bold text-white mt-1" data-testid="total-vehicle-value">{fmtCurrency(totalValue)}</p>
          <p className="text-blue-200 text-sm mt-1">{vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-20">
          <Car className="w-24 h-24 text-white" />
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vehicles..."
            className="pl-9" data-testid="vehicle-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setTypeFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${typeFilter === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid="vehicle-filter-all">All ({vehicles.length})</button>
          {uniqueTypes.map(t => (
            <button key={t} onClick={() => setTypeFilter(typeFilter === t ? 'all' : t)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${typeFilter === t ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid={`vehicle-filter-${t.toLowerCase()}`}>{t} ({vehicles.filter(v => v.vehicle_type === t).length})</button>
          ))}
        </div>
      </div>

      {/* Vehicle Cards Grid */}
      {filteredVehicles.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Car className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">{search || typeFilter !== 'all' ? 'No matches' : 'No vehicles yet'}</h3>
          <p className="text-gray-500 mb-4">{search || typeFilter !== 'all' ? 'Try a different search or filter' : 'Add your first vehicle to start tracking'}</p>
          {!search && typeFilter === 'all' && <Button onClick={() => setAddOpen(true)}><Plus className="w-4 h-4 mr-2" />Add Vehicle</Button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredVehicles.map(v => {
            const Icon = v.vehicle_type === 'Truck' ? Truck : Car;
            return (
              <div key={v.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedVehicle(v)} data-testid={`vehicle-card-${v.id}`}>
                {/* Card Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{v.year} {v.make}</h3>
                      <p className="text-sm text-gray-500">{v.model}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setEditVehicle(v)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" data-testid={`edit-vehicle-${v.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleteTarget(v)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`delete-vehicle-${v.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {v.tags?.map((tag, i) => (
                    <span key={i} className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">{tag}</span>
                  ))}
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{v.vehicle_type}</span>
                  {v.color && <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{v.color}</span>}
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      v.has_title
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}
                    data-testid={`vehicle-title-badge-${v.id}`}
                  >
                    {v.has_title ? 'Title' : 'No Title'}
                  </span>
                </div>

                {/* Value */}
                <p className="text-2xl font-bold text-gray-900 mb-3">{fmtCurrency(v.value)}</p>

                {/* Details */}
                <div className="space-y-1.5 text-sm border-t border-gray-100 pt-3">
                  {v.owner && <p className="text-gray-600"><span className="text-gray-400">Owner:</span> {v.owner}</p>}
                  {v.location && <p className="text-gray-600"><span className="text-gray-400">Location:</span> {v.location}</p>}
                  {v.insurance_company && <p className="text-gray-600"><span className="text-gray-400">Insurance:</span> {v.insurance_company}{v.policy_number ? ` (${v.policy_number})` : ''}</p>}
                  {v.vin && <p className="text-gray-600"><span className="text-gray-400">VIN:</span> {v.vin}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      <VehicleDialog open={addOpen || !!editVehicle} onOpenChange={v => { if (!v) { setAddOpen(false); setEditVehicle(null); } }}
        vehicle={editVehicle} companyId={currentCompany?.id} onSave={fetchVehicles} />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Vehicle</AlertDialogTitle>
            <AlertDialogDescription>Delete "{deleteTarget?.year} {deleteTarget?.make} {deleteTarget?.model}"? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
